import { useCallback, useState } from 'react';
import { Flex, useToast } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { GetParamsObject } from '@/model/query-params';
import { AccessControlFilters } from './types';
import { useDashboardUser, useDeleteDashboardUserMutation } from '@/services/queries/dashboard-user';
import { AccessControlFiltersBox } from './components/AccessControlFiltersBox';
import { AccessControlUsersTable } from './components/AccessControlUsersTable';
import { useNavigate } from 'react-router';
import { NewAccessControlUser } from './components/NewAccessControlUser';
import { useTranslation } from 'react-i18next';

export const AccessControl = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  const filtersForm = useFiltersForm<AccessControlFilters>({
    defaultValues: {
      name: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useDashboardUser(params);

  const deleteDashboardUser = useDeleteDashboardUserMutation();

  const onDelete = useCallback(
    (id: string) => {
      deleteDashboardUser.mutate(
        { id: id },
        {
          onSuccess: () => {
            forceRefetch();
            toast({
              status: 'success',
              title: t('pages.access_control.toast.delete_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.access_control.toast.delete_error'),
            });
          },
        },
      );
    },
    [deleteDashboardUser, forceRefetch, toast, t],
  );
  const itemsData = itemData?.data || [];

  return (
    <PageContent title={t('pages.access_control.title')} breadcrumbItems={breadcrumb}>
      <NewAccessControlUser />

      <SectionBox bodyProps={{ gap: 'small' }} mt={8}>
        <AccessControlFiltersBox filtersForm={filtersForm} forceRefetch={forceRefetch} />

        <Flex direction='column' gap='deka' mt={8}>
          <AccessControlUsersTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => navigate(`/access-control/users/${item.uuid}`)}
            onDeleteClick={item => onDelete(item.uuid)}
          />
        </Flex>
      </SectionBox>

      <CenteredSpinner isLoading={isLoading || deleteDashboardUser.isLoading} />
    </PageContent>
  );
};
